import { IconButton, Menu, toast } from "@carbon6/c6-frontend-design";
import { useCallback, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import {
  getSessionRetrainAvailability,
  isSessionDeletePending,
  renameTrainingSession,
  SESSION_DISPLAY_NAME_MAX_LENGTH,
  type TrainingSessionListView,
} from "@/api";
import { useAppState } from "@/app";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset/loadState";
import { HatTextModal } from "@/components/modal";
import { createLogger } from "@/logging";
import { SessionNameDialog } from "./SessionNameDialog";
import {
  applySessionDelete,
  deleteConfirmText,
  resolveSessionDeleteErrorMessage,
  sessionDeleteActionText,
} from "./sessionDeleteActions";
import {
  openRetrainWizardForSession,
  resolveCloneErrorMessage,
  resolveResumeErrorMessage,
  resolveRetrainDisabledReason,
  sessionStartActionText,
  startSessionFrom,
} from "./sessionStartActions";
import { derivedSessionDefaultName, sessionDisplayName } from "./trainingSessionFormat";
import { openTrainingSessionWindow } from "./trainingSessionWindow";
import { isResumableSessionStatus } from "@/modules/training/public";
import styles from "./SubmitSessionRowActions.module.css";

const logger = createLogger("page/training/rowActions");

const NOT_OWNER_REASON = "이 Session을 선점한 사용자나 admin 만 새 Session을 시작할 수 있습니다.";
const NOT_TERMINAL_REASON = "끝난 Training만 이어서 돌릴 수 있습니다.";
// 삭제는 즉시 하지 않고 유예 기간 뒤에 한다. 삭제 예정 세션에서 새 학습을 시작하거나 이름을
// 고치면 곧 사라질 세션을 바꾸게 된다. 그래서 먼저 복원하게 한다.
const DELETE_PENDING_REASON = "삭제 예정인 Session입니다. 먼저 복원해 주세요.";
const NOT_OWNER_ARCHIVE_REASON = "이 Session을 선점한 사용자나 admin 만 삭제·복원할 수 있습니다.";

type NameDialogKind = "clone" | "resume" | "rename";

const nameDialogText: Record<NameDialogKind, { title: string; hint: string; fieldLabel?: string }> =
  {
    clone: {
      title: "같은 설정으로 새 Session을 만듭니다.",
      hint: "이 Session의 시작 조건을 그대로 재현하는 새 Session을 만듭니다. 지금까지의 Training 진행은 이어받지 않습니다.",
    },
    resume: {
      title: "Checkpoint에서 이어서 학습합니다.",
      hint: "마지막 Checkpoint를 이어받는 새 Session을 만듭니다. 이 Session은 그대로 남고, Training 곡선은 두 Session에 걸쳐 이어집니다.",
    },
    rename: {
      title: "Training Session 이름을 바꿉니다.",
      hint: "이 Session의 표시 이름만 바뀝니다. Training 결과와 MLflow run 은 그대로입니다.",
      fieldLabel: "Session 이름",
    },
  };

const RENAME_SUBMIT_LABEL = "이름 변경";

type SubmitSessionRowActionsProps = {
  view: TrainingSessionListView;
  // 목록 행을 오래된 값으로 만드는 변경(이름·삭제 예정 상태) 뒤에 현재 페이지를 다시 받게 한다.
  onSessionChanged: () => void;
};

// 목록 행의 공유/다운로드 버튼과 ⋯ 메뉴. 버튼은 상세 Checkpoint 탭을 연다. 메뉴는 다음을 표시한다.
//   - 시작 액션 3종: 복제·이어서 학습·재학습
//   - 행 관리: 이름 변경·삭제/복원
// 문구와 실행 절차는 상세 헤더와 같은 소유자(sessionStartActions·sessionDeleteActions)를 쓴다.
//
// 재학습 가능 여부는 세션당 왕복 2회라 목록 전체를 미리 물을 수 없다. **메뉴를 연 행만** 한 번
// 묻는다. 답이 오기 전에는 항목을 "확인 중" 사유로 잠근다.
export function SubmitSessionRowActions({
  view,
  onSessionChanged,
}: SubmitSessionRowActionsProps): ReactNode {
  const { state: appState } = useAppState();
  const myMemberUuid = appState.currentUser?.memberUuid ?? null;
  const isOwner = myMemberUuid !== null && view.registry_user?.uuid === myMemberUuid;
  const isAdmin = appState.currentUser?.role === "admin";
  // clone·resume 은 부모 workspace 의 GPU 를 쓰므로 선점자/admin 게이트다. 재학습은 신규 제출
  // 경로라 선점 게이트를 거치지 않는다(워크스페이스는 위저드에서 새로 고른다).
  //
  // 삭제·복원도 같은 게이트로 좁힌다. 서버는 스코프 안 member 면 허용한다. 그러나 삭제는 실행 중인
  // 학습을 취소한다. 그래서 팀 스코프로 보이기만 하는 다른 사람의 세션을 지우지 못하게 한다.
  const canCommand = isOwner || isAdmin;
  const deleted = isSessionDeletePending(view);

  const [retrainState, setRetrainState] = useState<LoadState<boolean>>({ status: "idle" });
  const [dialog, setDialog] = useState<NameDialogKind | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const title = sessionDisplayName(view);
  const sessionId = view.id;

  const probeRetrain = useCallback(() => {
    setRetrainState({ status: "loading" });
    void getSessionRetrainAvailability(sessionId).then(
      (available) => setRetrainState({ status: "success", data: available }),
      (error: unknown) => {
        logger.error("retrain availability probe failed", error);
        setRetrainState({ status: "error", message: resolveLoadErrorMessage(error) });
      },
    );
  }, [sessionId]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // 한 번 답을 받았으면 다시 묻지 않는다. 체크포인트는 학습이 진행되면서 생길 뿐 사라지지
      // 않는다. 그래서 메뉴를 여닫을 때마다 cross-region 왕복을 반복할 이유가 없다. 삭제 예정 행은
      // 재학습이 잠겨 있으므로 묻지 않는다.
      if (open && !deleted && retrainState.status === "idle") {
        probeRetrain();
      }
    },
    [deleted, retrainState.status, probeRetrain],
  );

  const submitNameDialog = useCallback(
    (displayName: string) => {
      if (dialog === null || submitting) {
        return;
      }
      const kind = dialog;
      setSubmitting(true);
      setErrorMessage(null);
      const run =
        kind === "rename"
          ? renameTrainingSession(sessionId, { display_name: displayName }).then(() => {
              // 이름은 행이 보관한 값이다. 목록을 다시 받아야 바뀐 이름이 보인다.
              onSessionChanged();
            })
          : startSessionFrom(kind, { sessionId, displayName });
      void run
        .then(() => setDialog(null))
        .catch((error: unknown) => {
          // 모달을 연 채로 사유를 표시한다. 사용자가 이름을 다시 입력하지 않게 한다.
          setErrorMessage(resolveNameDialogError(kind, error));
        })
        .finally(() => setSubmitting(false));
    },
    [dialog, submitting, sessionId, onSessionChanged],
  );

  const submitDelete = useCallback(() => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    void applySessionDelete("delete", { sessionIds: [sessionId] })
      .then(() => {
        setConfirmingDelete(false);
        onSessionChanged();
      })
      .catch((error: unknown) => {
        logger.error("session delete failed", error);
        setErrorMessage(resolveSessionDeleteErrorMessage("delete", error));
      })
      .finally(() => setSubmitting(false));
  }, [submitting, sessionId, onSessionChanged]);

  // 복원은 되돌리는 방향이라 확인을 묻지 않는다. 실패하면 인라인 자리가 없으므로 토스트로 알린다.
  const restore = useCallback(() => {
    void applySessionDelete("restore", { sessionIds: [sessionId] })
      .then(() => onSessionChanged())
      .catch((error: unknown) => {
        logger.error("session restore failed", error);
        toast.error(resolveSessionDeleteErrorMessage("restore", error));
      });
  }, [sessionId, onSessionChanged]);

  const openRetrain = useCallback(() => {
    void openRetrainWizardForSession(sessionId, title).catch((error: unknown) => {
      logger.error("retrain wizard open failed", error);
      setRetrainState({ status: "error", message: resolveLoadErrorMessage(error) });
    });
  }, [sessionId, title]);

  const resumable = isResumableSessionStatus(view.status);
  const startDisabledReason = deleted
    ? DELETE_PENDING_REASON
    : canCommand
      ? null
      : NOT_OWNER_REASON;
  const retrainDisabledReason = deleted
    ? DELETE_PENDING_REASON
    : resolveRetrainDisabledReason(retrainState);
  const cloneReason = startDisabledReason ?? sessionStartActionText.clone.hint;
  const resumeReason =
    startDisabledReason ?? (resumable ? sessionStartActionText.resume.hint : NOT_TERMINAL_REASON);
  const renameReason = deleted ? DELETE_PENDING_REASON : "이 Session의 표시 이름을 바꿉니다.";
  const deleteKind = deleted ? "restore" : "delete";
  const deleteReason = canCommand
    ? sessionDeleteActionText[deleteKind].hint
    : NOT_OWNER_ARCHIVE_REASON;
  const confirmText = deleteConfirmText(1);

  return (
    // 행 클릭은 상세 창을 연다. 그래서 메뉴 조작이 행으로 전파되지 않게 여기서 막는다.
    <span
      className={styles.rowActions}
      onClick={stopRowClick}
      onPointerDown={stopRowClick}
      role="presentation"
    >
      <IconButton
        text="공유/다운로드"
        type="button"
        size="sm"
        variant="surface"
        title="Training 중 저장된 Model(Checkpoint)을 공유하거나 다운로드할 수 있는 Checkpoint 탭을 엽니다."
        onClick={() => openTrainingSessionWindow(view, { focus: { tab: "checkpoints" } })}
      />
      <Menu label={`${title} 작업`} onOpenChange={handleOpenChange}>
        <Menu.Trigger label="더 보기" />
        <Menu.Item
          disabled={startDisabledReason !== null}
          caption={cloneReason}
          onSelect={() => {
            setErrorMessage(null);
            setDialog("clone");
          }}
        >
          {sessionStartActionText.clone.label}
        </Menu.Item>
        <Menu.Item
          disabled={startDisabledReason !== null || !resumable}
          caption={resumeReason}
          onSelect={() => {
            setErrorMessage(null);
            setDialog("resume");
          }}
        >
          {sessionStartActionText.resume.label}
        </Menu.Item>
        <Menu.Item
          disabled={retrainDisabledReason !== null}
          caption={retrainDisabledReason ?? sessionStartActionText.retrain.hint}
          onSelect={openRetrain}
        >
          {sessionStartActionText.retrain.label}
        </Menu.Item>
        <Menu.Item
          disabled={deleted}
          caption={renameReason}
          onSelect={() => {
            setErrorMessage(null);
            setDialog("rename");
          }}
        >
          {RENAME_SUBMIT_LABEL}
        </Menu.Item>
        <Menu.Item
          disabled={!canCommand}
          caption={deleteReason}
          onSelect={() => {
            setErrorMessage(null);
            if (deleted) {
              restore();
              return;
            }
            setConfirmingDelete(true);
          }}
        >
          {sessionDeleteActionText[deleteKind].label}
        </Menu.Item>
      </Menu>
      {dialog === null ? null : (
        <SessionNameDialog
          title={nameDialogText[dialog].title}
          hint={nameDialogText[dialog].hint}
          fieldLabel={nameDialogText[dialog].fieldLabel}
          submitLabel={
            dialog === "rename" ? RENAME_SUBMIT_LABEL : sessionStartActionText[dialog].label
          }
          pendingLabel={dialog === "rename" ? "바꾸는 중…" : "시작하는 중…"}
          defaultName={
            dialog === "rename"
              ? title
              : derivedSessionDefaultName(title, SESSION_DISPLAY_NAME_MAX_LENGTH)
          }
          submitting={submitting}
          errorMessage={errorMessage}
          onClose={() => {
            if (!submitting) {
              setDialog(null);
              setErrorMessage(null);
            }
          }}
          onSubmit={submitNameDialog}
        />
      )}
      <HatTextModal
        open={confirmingDelete}
        label={confirmText.title}
        eyebrow={title}
        caption={confirmText.caption}
        error
        okText={sessionDeleteActionText.delete.label}
        busy={submitting}
        errorMessage={errorMessage ?? undefined}
        onOkPress={submitDelete}
        onCancelPress={() => {
          if (!submitting) {
            setConfirmingDelete(false);
            setErrorMessage(null);
          }
        }}
      />
    </span>
  );
}

// 이름 모달의 실패 문구. 세 동작은 거절 코드의 뜻을 서로 다르게 읽는다. rename 은 상태 제약이
// 없어 공통 문구로 충분하다.
function resolveNameDialogError(kind: NameDialogKind, error: unknown): string {
  switch (kind) {
    case "clone":
      return resolveCloneErrorMessage(error);
    case "resume":
      return resolveResumeErrorMessage(error);
    case "rename":
      return resolveLoadErrorMessage(error);
  }
}

function stopRowClick(event: ReactMouseEvent<HTMLElement>): void {
  event.stopPropagation();
}
